/**
 * Webhook Realtime Processor
 */

import { supabase } from '@/integrations/supabase/client';
import { getCurrentWorkspaceId } from './workspaceState';
import { contactService, conversationService, messageService, logService, aiService, templateService } from './dataService';
import { triggerAutomations } from './automationEngine';
import { _updateMessageDeliveryState, _updateMessageFields } from './whatsappService';
import { notifyIncomingMessage } from './notificationSound';
import {
  evaluateWhatsAppTriggers, normalizeMessageText, isFirstIncomingMessage, extractReferralMetadata,
  triggerService,
} from './triggerService';
import type { TriggerEventContext } from './triggerService';
import { executeAutomation } from './automationEngine';
import { automationService } from './dataService';
import type { DeliveryState, WhatsAppFailureDetails } from '../types';

/** Map common WhatsApp error codes to readable labels */
function getReadableFailureLabel(code?: number, title?: string): string {
  const labels: Record<number, string> = {
    131047: '24h window expired — customer hasn\'t replied recently',
    131051: 'Message type not supported',
    131026: 'Message undeliverable (recipient issue)',
    131053: 'Media upload error',
    131042: 'Business eligibility issue',
    131031: 'Account locked / restricted',
    131056: 'Pair rate limit hit — too many messages',
    131048: 'Spam rate limit reached',
    131021: 'Recipient not a valid WhatsApp user',
    131009: 'Parameter missing or invalid',
    132000: 'Template parameter count mismatch',
    132001: 'Template does not exist',
    132005: 'Template text too long',
    132007: 'Template format error',
    132012: 'Template paused',
    132015: 'Template rejected',
    131000: 'Generic downstream error',
  };
  if (code && labels[code]) return labels[code];
  if (title) return title;
  return 'Message delivery failed';
}

let channel: ReturnType<typeof supabase.channel> | null = null;
let isStarted = false;
const inFlightEventIds = new Set<string>(); // Prevent concurrent double-processing

/** Simple local intent classification from text patterns */
function classifyIntentLocally(text: string): { intent: string; confidence: number } {
  const t = text.toLowerCase().trim();
  const patterns: [string, RegExp[], number][] = [
    ['greeting', [/^(hi|hello|hey|salam|marhba|bonjour|salut)/i, /^(good\s*(morning|evening|afternoon))/i], 0.85],
    ['product_inquiry', [/product|item|catalog|what.*sell|available|stock|collection/i], 0.8],
    ['purchase_intent', [/buy|order|purchase|want.*to.*get|add.*cart|checkout/i], 0.82],
    ['order_tracking', [/track|where.*order|status.*order|deliver|shipment|shipping/i], 0.83],
    ['complaint', [/complain|problem|issue|bad|terrible|worst|angry|disappointed/i], 0.78],
    ['return_request', [/return|refund|exchange|send.*back|money.*back/i], 0.8],
    ['pricing_question', [/price|cost|how much|expensive|cheap|discount|offer|promo/i], 0.81],
    ['delivery_question', [/deliver|shipping|when.*arrive|how long|estimated/i], 0.79],
  ];
  for (const [intent, regexes, conf] of patterns) {
    if (regexes.some(r => r.test(t))) return { intent, confidence: conf };
  }
  return { intent: 'unknown', confidence: 0.3 };
}

/**
 * Start listening for real webhook events via Supabase Realtime.
 */
export function startWebhookListener(): void {
  if (isStarted) return;
  isStarted = true;

  const wsId = getCurrentWorkspaceId();
  const wsFilter = wsId ? `workspace_id=eq.${wsId}` : undefined;

  channel = supabase
    .channel('webhook-events')
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'webhook_events',
        ...(wsFilter ? { filter: wsFilter } : {}),
      },
      async (payload) => {
        const event = payload.new as {
          id: string;
          event_type: string;
          payload: any;
          processed: boolean;
        };

        if (event.processed || inFlightEventIds.has(event.id)) return;
        inFlightEventIds.add(event.id);

        try {
          if (event.event_type === 'message') {
            // IMPORTANT: inbound message persistence is already handled server-side
            // in whatsapp-webhook edge function (contact + conversation + message).
            // Re-processing here creates duplicate local entities.
            await markEventProcessed(event.id);
            return;
          } else if (event.event_type === 'status') {
            processStatusEvent(event.id, event.payload);
          } else if (event.event_type === 'template_status') {
            processTemplateStatusEvent(event.id, event.payload);
          }

          // Mark as processed
          await markEventProcessed(event.id);
        } catch (err) {
          console.error('Error processing webhook event:', err);
          logService.add('whatsapp', 'webhook_process_error', `Failed to process webhook event ${event.id}: ${(err as Error).message}`, {
            eventId: event.id, eventType: event.event_type, error: (err as Error).message,
          }, 'error');
        } finally {
          inFlightEventIds.delete(event.id);
        }
      }
    )
    .subscribe((status) => {
      console.log('Webhook realtime subscription:', status);
    });

  // Also process any unprocessed events that arrived while offline
  processBacklog();
}

/**
 * Stop the webhook listener.
 */
export function stopWebhookListener(): void {
  if (channel) {
    supabase.removeChannel(channel);
    channel = null;
  }
  isStarted = false;
}

/**
 * Process any unprocessed events from the database (backlog).
 */
async function processBacklog(): Promise<void> {
  try {
    const wsId = getCurrentWorkspaceId();
    let query = supabase
      .from('webhook_events')
      .select('*')
      .eq('processed', false);
    if (wsId) query = query.eq('workspace_id', wsId);
    const { data: events, error } = await query
      .order('created_at', { ascending: true })
      .limit(100);

    if (error) {
      console.warn('Failed to fetch webhook backlog:', error);
      return;
    }

    if (!events || events.length === 0) return;

    console.log(`Processing ${events.length} backlog webhook events`);

    for (const event of events) {
      if (inFlightEventIds.has(event.id)) continue;
      inFlightEventIds.add(event.id);

      try {
        if (event.event_type === 'message') {
          // Already persisted server-side by whatsapp-webhook edge function
          await markEventProcessed(event.id);
          continue;
        } else if (event.event_type === 'status') {
          processStatusEvent(event.id, event.payload);
        } else if (event.event_type === 'template_status') {
          processTemplateStatusEvent(event.id, event.payload);
        }
        await markEventProcessed(event.id);
      } catch (err) {
        console.error(`Failed to process backlog event ${event.id}:`, err);
      } finally {
        inFlightEventIds.delete(event.id);
      }
    }
  } catch (err) {
    console.warn('Backlog processing error:', err);
  }
}

/**
 * Process an incoming message webhook event.
 */
async function processIncomingMessageEvent(eventId: string, payload: any): Promise<void> {
  const phoneNumber = String(payload.from || '').replace(/\D/g, '');
  const text = payload.text || '';
  const contactName = payload.contact_name || `WhatsApp User ${phoneNumber.slice(-4)}`;
  const wamid = payload.wamid;
  const messageType = payload.type || 'text';

  if (!phoneNumber || (!text && messageType === 'text')) {
    console.warn('Skipping empty message event:', eventId);
    return;
  }

  // Find or create contact using canonical digits-only phone identity
  let contact = contactService.getByPhone(phoneNumber);
  let isNewContact = false;

  if (!contact) {
    const id = contactService.create({
      fullName: contactName,
      phoneNumber,
      source: 'whatsapp_incoming',
      leadStatus: 'new',
      tags: ['whatsapp'],
    });
    contact = contactService.getById(id)!;
    isNewContact = true;
  }

  // Find or create conversation
  const existingConv = conversationService.getByContactId(contact.id);
  const convId = existingConv
    ? existingConv.id
    : conversationService.create({ contactId: contact.id, channelType: 'whatsapp_api', status: 'open' });
  const isNewConversation = !existingConv;

  // Reopen if closed
  const conv = conversationService.getById(convId);
  if (conv && conv.status === 'closed') {
    conversationService.update(convId, { status: 'open' });
  }

  // Determine body and media based on message type
  let body = text;
  let resolvedType: 'text' | 'image' | 'voice' = 'text';
  let mediaUrl: string | undefined;
  let mediaId: string | undefined;

  if (messageType === 'image') {
    body = payload.image?.caption || '📷 Image';
    resolvedType = 'image';
    mediaId = payload.image?.id;
  } else if (messageType === 'audio') {
    body = '🎙️ Voice message';
    resolvedType = 'voice';
    mediaId = payload.audio?.id;
  } else if (messageType === 'video') { body = '🎥 Video'; }
  else if (messageType === 'document') { body = '📄 Document'; }

  // Create inbound message first (optimistic - shows immediately with loading state)
  const msg = messageService.create({
    conversationId: convId,
    contactId: contact.id,
    direction: 'inbound',
    messageType: resolvedType,
    body,
    deliveryState: 'delivered',
    externalMessageId: wamid,
    mediaUrl: undefined, // Will be set after download
  });

  logService.add('whatsapp', 'whatsapp_message_received',
    `Real incoming from ${contact.fullName}: "${body.substring(0, 60)}"`, {
      conversationId: convId, contactId: contact.id, messageId: msg.id,
      phoneNumber, wamid, mode: 'webhook', eventId, mediaType: messageType, mediaId,
    }, 'info', 'message', msg.id);

  // Download and store media if it's a media message
  if (mediaId && (resolvedType === 'image' || resolvedType === 'voice')) {
    downloadAndStoreMedia(convId, msg.id, contact.id, mediaId, resolvedType, wamid);
  }

  // Sound + browser notification
  notifyIncomingMessage(contact.fullName, body);

  // ─── WhatsApp Trigger Evaluation Pipeline ───
  const referral = extractReferralMetadata(payload);
  const triggerEventCtx: TriggerEventContext = {
    contactId: contact.id,
    conversationId: convId,
    messageId: msg.id,
    text: body,
    normalizedText: normalizeMessageText(body),
    referral,
    timestamp: new Date().toISOString(),
    isFirstMessage: isFirstIncomingMessage(contact.id, convId),
    messageType: resolvedType,
  };

  // Run AI intent classification for intent-based triggers
  let intentResult: { intent: string; confidence: number } | undefined;
  const intentTriggers = triggerService.getActiveByType('inbound_message')
    .filter(t => (t.config as any).mode === 'intent');
  if (intentTriggers.length > 0 && body) {
    // Simple local intent classification based on keyword patterns
    intentResult = classifyIntentLocally(body);
  }

  // Evaluate all active WhatsApp triggers
  const evalResult = evaluateWhatsAppTriggers(triggerEventCtx, intentResult);

  // Log matched triggers for diagnostics (execution is handled server-side)
  for (const match of evalResult.matches) {
    const flow = automationService.getById(match.automationId);
    if (flow && flow.status === 'active') {
      logService.add('triggers', 'trigger_matched_client',
        `Trigger ${match.triggerType} matched automation "${flow.name}" (server will execute)`, {
          triggerId: match.triggerId, automationId: match.automationId,
          contactId: contact.id, conversationId: convId, messageId: msg.id,
          matchedKeyword: match.matchedKeyword, matchedIntent: match.matchedIntent,
          matchedAdId: match.matchedAdId, matchedCampaignCode: match.matchedCampaignCode,
        }, 'info', 'automation', flow.id);
      // Do NOT call executeAutomation here — the server-side webhook already does this
    }
  }

  // IMPORTANT: Do NOT trigger client-side automations OR AI auto-reply for WhatsApp inbound messages.
  // The server-side webhook (whatsapp-webhook edge function) already:
  //   1. Evaluates triggers and invokes the automation-executor
  //   2. Sends AI auto-reply if fullAutoReply is enabled and no trigger matched
  // Running them here would cause DUPLICATE execution/replies.
}

/**
 * Process a status update webhook event.
 */
function processStatusEvent(eventId: string, payload: any): void {
  const wamid = payload.wamid;
  const status = payload.status;

  if (!wamid || !status) return;

  const stateMap: Record<string, DeliveryState> = {
    sent: 'sent',
    delivered: 'delivered',
    read: 'read',
    failed: 'failed',
  };
  const newState = stateMap[status];
  if (!newState) return;

  // Extract failure details if status is failed
  let failureDetails: WhatsAppFailureDetails | undefined;
  if (newState === 'failed' && payload.errors && Array.isArray(payload.errors) && payload.errors.length > 0) {
    const err = payload.errors[0];
    failureDetails = {
      errorCode: err.code,
      errorTitle: err.title,
      errorMessage: err.message,
      errorDetails: err.error_data?.details,
      readableLabel: getReadableFailureLabel(err.code, err.title),
    };
  }

  // Find message by external ID across all conversations
  const conversations = conversationService.getAll();
  for (const conv of conversations) {
    const msgs = messageService.getByConversation(conv.id);
    const msg = msgs.find(m => m.externalMessageId === wamid);
    if (msg) {
      if (failureDetails) {
        _updateMessageFields(conv.id, msg.id, { deliveryState: newState, failureDetails });
      } else {
        _updateMessageDeliveryState(conv.id, msg.id, newState);
      }

      const logLevel = newState === 'failed' ? 'error' : 'info';
      const statusLogNames: Record<string, string> = {
        sent: 'whatsapp_status_sent',
        delivered: 'whatsapp_status_delivered',
        read: 'whatsapp_status_read',
        failed: 'message_failed',
      };
      const logEventName = statusLogNames[newState] || 'status_updated';
      const logMsg = newState === 'failed' && failureDetails
        ? `Message ${msg.id} FAILED: [${failureDetails.errorCode}] ${failureDetails.readableLabel}`
        : `Message ${msg.id} status → ${newState} (webhook)`;

      logService.add('whatsapp', logEventName,
        logMsg, {
          messageId: msg.id, wamid, status: newState, eventId,
          ...(failureDetails ? { failureDetails } : {}),
        }, logLevel, 'message', msg.id);
      break;
    }
  }
}

/**
 * Download media from WhatsApp via edge function and store in Supabase Storage.
 * Updates the message record with the permanent public URL.
 */
async function downloadAndStoreMedia(
  conversationId: string,
  messageId: string,
  contactId: string,
  mediaId: string,
  mediaType: 'image' | 'voice',
  wamid?: string
): Promise<void> {
  try {
    const { integrationService: intSvc } = await import('./dataService');
    const accessToken = intSvc.get().whatsapp?.accessToken;

    if (!accessToken) {
      console.error('media_download_skipped', { messageId, reason: 'No access token' });
      logService.add('whatsapp', `${mediaType}_fetch_failed`, `No access token`, {
        messageId, conversationId, contactId, mediaId, mediaType,
      }, 'error', 'message', messageId);
      return;
    }

    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || `https://${projectId}.supabase.co`;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    console.log(`${mediaType}_download_started`, { messageId, conversationId, contactId, mediaId });

    const response = await fetch(`${supabaseUrl}/functions/v1/whatsapp-media-proxy`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ mediaId, accessToken, conversationId, messageId, mediaType }),
    });

    const result = await response.json();

    if (!result.success || !result.publicUrl) {
      console.error(`${mediaType}_download_failed`, { messageId, error: result.error });
      logService.add('whatsapp', `${mediaType}_fetch_failed`,
        `Failed to download ${mediaType}: ${result.error}`, {
          messageId, conversationId, contactId, mediaId, error: result.error,
        }, 'error', 'message', messageId);
      return;
    }

    // Update message with permanent public URL
    _updateMessageFields(conversationId, messageId, { mediaUrl: result.publicUrl });

    console.log(`${mediaType}_download_completed`, {
      messageId, conversationId, publicUrl: result.publicUrl.substring(0, 80),
      mimeType: result.mimeType, fileSize: result.fileSize,
    });

    logService.add('whatsapp', `${mediaType}_received`,
      `${mediaType === 'image' ? 'Image' : 'Audio'} downloaded and stored`, {
        messageId, conversationId, contactId, mediaId, wamid,
        storagePath: result.storagePath, mimeType: result.mimeType, fileSize: result.fileSize,
      }, 'success', 'message', messageId);

  } catch (err: any) {
    console.error(`${mediaType}_download_error`, { messageId, error: err.message });
    logService.add('whatsapp', `${mediaType}_fetch_failed`,
      `Media download error: ${err.message}`, {
        messageId, conversationId, contactId, mediaId, error: err.message,
      }, 'error', 'message', messageId);
  }
}

/**
 * Upload a local file to Supabase Storage and return the public URL.
 * Normalizes MIME types to ensure WhatsApp compatibility.
 */

// WhatsApp-accepted audio MIME types
const WHATSAPP_AUDIO_MIMES = ['audio/ogg', 'audio/mpeg', 'audio/amr', 'audio/mp4', 'audio/aac'];

function normalizeAudioMime(blob: Blob): { mime: string; ext: string } {
  const raw = blob.type || '';
  const base = raw.split(';')[0].trim().toLowerCase();

  // Map known browser-produced types to WhatsApp-compatible ones
  if (base === 'audio/ogg' || raw.includes('opus')) return { mime: 'audio/ogg', ext: 'ogg' };
  if (base === 'audio/webm') return { mime: 'audio/webm', ext: 'webm' }; // needs conversion
  if (base === 'audio/mpeg' || base === 'audio/mp3') return { mime: 'audio/mpeg', ext: 'mp3' };
  if (base === 'audio/mp4' || base === 'audio/m4a' || base === 'audio/x-m4a') return { mime: 'audio/mp4', ext: 'm4a' };
  if (base === 'audio/amr') return { mime: 'audio/amr', ext: 'amr' };
  if (base === 'audio/aac') return { mime: 'audio/aac', ext: 'aac' };
  // Fallback: treat unknown audio as ogg (voice recordings are usually opus)
  if (base.startsWith('audio/')) return { mime: 'audio/ogg', ext: 'ogg' };
  return { mime: base || 'application/octet-stream', ext: 'bin' };
}

export async function uploadMediaToStorage(
  file: Blob,
  conversationId: string,
  messageId: string,
  mediaType: 'image' | 'voice' | 'document'
): Promise<{ publicUrl: string; storagePath: string; mimeType: string } | null> {
  try {
    const { getCurrentWorkspaceId } = await import('./workspaceState');
    const workspaceId = getCurrentWorkspaceId() || 'default';

    let uploadMime: string;
    let ext: string;

    if (mediaType === 'voice' || file.type?.startsWith('audio/')) {
      const normalized = normalizeAudioMime(file);
      uploadMime = normalized.mime;
      ext = normalized.ext;
      console.log('audio_mime_normalized', { original: file.type, normalized: uploadMime, ext });
    } else {
      const rawMime = file.type || (mediaType === 'image' ? 'image/jpeg' : 'application/octet-stream');
      uploadMime = rawMime.split(';')[0].trim();
      const extMap: Record<string, string> = {
        'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/gif': 'gif',
        'application/pdf': 'pdf',
      };
      ext = extMap[uploadMime] || 'bin';
    }

    const storagePath = `${workspaceId}/${conversationId}/${messageId}.${ext}`;

    // Re-create blob with explicit correct MIME to prevent storage from using octet-stream
    const arrayBuffer = await file.arrayBuffer();
    const cleanBlob = new Blob([arrayBuffer], { type: uploadMime });

    console.log('media_upload_started', { messageId, conversationId, mediaType, mimeType: uploadMime, size: file.size });

    const { error: uploadError } = await supabase.storage
      .from('message-media')
      .upload(storagePath, cleanBlob, { contentType: uploadMime, upsert: true });

    if (uploadError) {
      console.error('media_upload_failed', { messageId, error: uploadError.message });
      logService.add('whatsapp', `${mediaType}_upload_failed`,
        `Upload failed: ${uploadError.message}`, { messageId, conversationId, error: uploadError.message },
        'error', 'message', messageId);
      return null;
    }

    // Create signed URL (bucket is private) — valid for 7 days
    const { data: signedUrlData, error: signedErr } = await supabase.storage
      .from('message-media')
      .createSignedUrl(storagePath, 60 * 60 * 24 * 7);

    const publicUrl = signedErr ? null : signedUrlData?.signedUrl;
    if (!publicUrl) return null;

    console.log('media_upload_completed', { messageId, conversationId, storagePath, mimeType: uploadMime });

    logService.add('whatsapp', `${mediaType}_upload_completed`,
      `${mediaType} uploaded successfully`, {
        messageId, conversationId, storagePath, mimeType: uploadMime, fileSize: file.size,
      }, 'success', 'message', messageId);

    return { publicUrl, storagePath, mimeType: uploadMime };
  } catch (err: any) {
    console.error('media_upload_error', { messageId, error: err.message });
    return null;
  }
}

/**
 * Process a template status webhook event.
 */
function processTemplateStatusEvent(_eventId: string, payload: any): void {
  console.log('Processing template status event:', payload);
  templateService.processTemplateStatusEvent(payload);
}

/**
 * Mark a webhook event as processed.
 */
async function markEventProcessed(eventId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('webhook_events')
      .update({ processed: true })
      .eq('id', eventId);
    if (error) {
      console.warn('Could not mark event as processed:', error);
    }
  } catch {
    console.warn('Could not mark event as processed:', eventId);
  }
}
